// =============================================================================
// Timezone Utilities
// =============================================================================
// All timestamps in Supabase are stored as UTC.
// This module converts UTC timestamps to a target IANA timezone for display
// and computes timezone-aware date boundaries for queries.

/**
 * Get date/time parts of a UTC timestamp in a specific timezone.
 * Returns { year, month (0-based), date, hours, minutes, seconds, day (0=Sun) }.
 */
export function getPartsInTZ(
  utcISO: string,
  tz: string
): {
  year: number;
  month: number;
  date: number;
  hours: number;
  minutes: number;
  seconds: number;
  day: number; // day of week 0-6
} {
  // ONE cached formatter per tz, read via formatToParts. The previous shape built
  // SEVEN `new Intl.DateTimeFormat(...)` per call (six in a `fmt` helper + one for
  // weekday); constructing a formatter re-negotiates locale data and is ~50-100us.
  // claimMetrics calls this per dial at three sites, so Rayston's 2,696 dials cost
  // ~38k constructions and 4,738ms of frozen main thread on the Claims tab
  // (CPU profile, 2026-07-22). Output is byte-identical — verified 28,000/28,000
  // against real dial_dials timestamps across 7 zones, plus 3,360/3,360 on
  // startOfDayInTZ/endOfDayInTZ/getDateKeyInTZ, which is what makes this safe to
  // ship WITHOUT a /cachebump (org-tz cache keys per cacheVersion v67 are unchanged).
  const parts = partsFormatterFor(tz).formatToParts(new Date(utcISO));
  const p: Record<string, string> = {};
  for (const part of parts) p[part.type] = part.value;

  // `hour12: false` can emit "24" for local midnight in some ICU versions.
  let hours = parseInt(p.hour, 10);
  if (hours === 24) hours = 0;

  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  return {
    year: parseInt(p.year, 10),
    month: parseInt(p.month, 10) - 1, // 0-based
    date: parseInt(p.day, 10),
    hours,
    minutes: parseInt(p.minute, 10),
    seconds: parseInt(p.second, 10),
    day: dayMap[p.weekday] ?? 0,
  };
}

// Formatter cache, keyed by IANA tz. Bounded by the number of zones an org can
// have (~hundreds worst case). An invalid tz throws during construction, BEFORE
// the .set(), so a bad zone can never poison the cache.
const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatterFor(tz: string): Intl.DateTimeFormat {
  let f = partsFormatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
      weekday: "short",
    });
    partsFormatterCache.set(tz, f);
  }
  return f;
}

/**
 * Format a UTC ISO timestamp as a time string in a given timezone.
 * e.g. "2:30 PM"
 */
export function formatTimeInTZ(utcISO: string, tz: string): string {
  const d = new Date(utcISO);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  });
}

/**
 * Format a UTC ISO timestamp as "Mon DD, H:MM AM" in a given timezone.
 * e.g. "Feb 14, 2:30 PM"
 */
export function formatDateTimeInTZ(utcISO: string, tz: string): string {
  return new Date(utcISO).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  });
}

/**
 * Get the UTC ISO string for midnight (start of day) in a given timezone.
 * E.g. midnight Jan 28 in Asia/Singapore = Jan 27 16:00 UTC
 */
export function startOfDayInTZ(year: number, month: number, date: number, tz: string): Date {
  // Create a rough UTC date, then adjust
  // Strategy: format a known date to find the UTC offset, then compute
  const guess = new Date(Date.UTC(year, month, date, 12, 0, 0)); // noon UTC as starting guess
  const parts = getPartsInTZ(guess.toISOString(), tz);

  // How far off are we from midnight in that timezone?
  const diffHours = parts.hours;
  const diffMinutes = parts.minutes;

  // Adjust: subtract the local time to get to midnight local
  const result = new Date(guess.getTime() - (diffHours * 60 + diffMinutes) * 60 * 1000);

  // Verify the date is correct (DST edge cases)
  const check = getPartsInTZ(result.toISOString(), tz);
  if (check.date !== date) {
    // Off by a day, adjust by 24h in the right direction
    const dir = check.date < date ? 1 : -1;
    result.setTime(result.getTime() + dir * 24 * 60 * 60 * 1000);
    // Re-adjust to midnight
    const p2 = getPartsInTZ(result.toISOString(), tz);
    result.setTime(result.getTime() - (p2.hours * 60 + p2.minutes) * 60 * 1000);
  }

  return result;
}

/**
 * Get the UTC ISO string for end of day (23:59:59.999) in a given timezone.
 */
export function endOfDayInTZ(year: number, month: number, date: number, tz: string): Date {
  const start = startOfDayInTZ(year, month, date, tz);
  return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
}

/**
 * Get the day-of-week key (date string) for a UTC timestamp in a timezone.
 */
export function getDateKeyInTZ(utcISO: string, tz: string): string {
  const p = getPartsInTZ(utcISO, tz);
  // Return a consistent key like "2026-01-28"
  return `${p.year}-${String(p.month + 1).padStart(2, "0")}-${String(p.date).padStart(2, "0")}`;
}

/**
 * Get a date key for a local Date object (just year-month-date).
 */
export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// =============================================================================
// Org-timezone-aware date boundary helpers
// =============================================================================
// The date picker creates Date objects at browser-local midnight. When these
// are converted to UTC via .toISOString(), different browser timezones produce
// different UTC instants for the "same" calendar day. These helpers extract
// the calendar day the user clicked (year/month/date from browser-local getters)
// and convert to the correct UTC boundary using the org's IANA timezone.

const DEFAULT_TZ = "UTC";

/**
 * Convert a picker Date to the UTC ISO string for start-of-day in the org timezone.
 * e.g. User clicks "March 1", org is Asia/Singapore →
 *   midnight Mar 1 Singapore = Feb 28 16:00 UTC → "2026-02-28T16:00:00.000Z"
 */
export function toOrgStartISO(d: Date, tz: string | null | undefined): string {
  const zone = tz || DEFAULT_TZ;
  return startOfDayInTZ(d.getFullYear(), d.getMonth(), d.getDate(), zone).toISOString();
}

/**
 * Convert a picker Date to the UTC ISO string for end-of-day (23:59:59.999) in the org timezone.
 */
export function toOrgEndISO(d: Date, tz: string | null | undefined): string {
  const zone = tz || DEFAULT_TZ;
  return endOfDayInTZ(d.getFullYear(), d.getMonth(), d.getDate(), zone).toISOString();
}

/**
 * Convert a picker Date to a YYYY-MM-DD date key using the calendar day the user selected.
 * This is timezone-stable: it reads the browser-local year/month/date (which matches
 * what the user clicked in the calendar) regardless of UTC offset.
 * Use for querying date columns (daily_org_metrics.date) and cache keys.
 */
export function toOrgDateKey(d: Date): string {
  // The picker creates dates as new Date(year, month, day) in browser-local time.
  // The calendar day the user intended is always the browser-local date components.
  return localDateKey(d);
}

/**
 * Convert a UTC timestamp string to a YYYY-MM-DD date key in the org timezone.
 * Use when grouping DB timestamps (event_date, call_start_time) into calendar days.
 */
export function timestampToOrgDateKey(utcISO: string, tz: string | null | undefined): string {
  return getDateKeyInTZ(utcISO, tz || DEFAULT_TZ);
}

// =============================================================================
// Display formatters with timezone
// =============================================================================

/**
 * Format a UTC ISO timestamp as a date-only string in a given timezone.
 * e.g. "Mar 20, 2026" — no time component.
 * Use for date columns, deal dates, event dates where time is irrelevant.
 */
export function formatDateInTZ(utcISO: string, tz: string, opts?: { short?: boolean }): string {
  return new Date(utcISO).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(opts?.short ? {} : { year: "numeric" }),
    timeZone: tz,
  });
}

/**
 * Format a YYYY-MM-DD date string safely for display in a given timezone.
 * Appends T12:00:00Z to avoid midnight UTC rollback, then formats with the given tz.
 * Use for daily aggregate date labels (chart axes, leaderboard breakdown rows).
 */
export function formatDateKeyDisplay(
  dateStr: string,
  tz: string,
  opts?: { weekday?: boolean }
): string {
  return new Date(dateStr + "T12:00:00Z").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(opts?.weekday ? { weekday: "short" } : {}),
    timeZone: tz,
  });
}

// Intl returns "GMT+8" for these zones instead of a conventional abbreviation.
// DST-affected zones (AU, US) stay on Intl so the abbreviation flips AEST↔AEDT / EST↔EDT correctly.
const TZ_ABBR_OVERRIDES: Record<string, string> = {
  "Asia/Singapore": "SGT",
  "Asia/Jakarta": "WIB",
  "Asia/Manila": "PHT",
  "Asia/Bangkok": "ICT",
  "Asia/Hong_Kong": "HKT",
  "Asia/Kuala_Lumpur": "MYT",
  "Asia/Ho_Chi_Minh": "ICT",
  "Asia/Taipei": "CST",
};

/**
 * Get the short timezone abbreviation for an IANA timezone.
 * e.g. "Asia/Singapore" → "SGT", "Australia/Sydney" → "AEST"
 */
export function getTimezoneAbbr(tz: string): string {
  if (TZ_ABBR_OVERRIDES[tz]) return TZ_ABBR_OVERRIDES[tz];
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "short",
  }).formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value || tz;
}

/**
 * Get the viewer's IANA timezone from the browser.
 * e.g. "Australia/Sydney", "Asia/Manila", "America/New_York"
 * No permission prompt — reads OS timezone setting.
 */
export function getViewerTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
